/**
 * Meta Tracking helpers — CostControlX
 *
 * Env-gated wrapper attorno al Meta Pixel + a un endpoint interno che
 * inoltra eventi anche via Conversions API. Sicuro per default:
 *
 *   - se `REACT_APP_META_PIXEL_ID` o `REACT_APP_ENABLE_META_TRACKING`
 *     non sono valorizzati, ogni chiamata è **no-op** silenziosa;
 *   - non esistono ID/token hardcoded;
 *   - deduplica Pixel/CAPI condividendo `event_id` (UUID v4);
 *   - non forza tracking prima del consenso: se hai un cookie consent
 *     helper valorizza `window.__ccx_marketing_consent = true`;
 *   - non tocca signup/login/trial/Stripe.
 *
 * API pubblica:
 *   initMetaTracking()             — inject Pixel <script> una sola volta
 *   trackPageView({page})          — event PageView + event_id dedup
 *   trackViewContent({page,...})   — event ViewContent (es. su /demo)
 *   trackLead({source,...})        — event Lead (richiesta demo)
 *   trackContact({channel,...})    — event Contact (WhatsApp/email/tel)
 *   trackCompleteRegistration({...}) — signup finito
 *   trackStartTrial({...})         — trial attivato
 *   trackSchedule({...})           — call/demo prenotata
 *   getTrackingStatus()            — { enabled, pixelId, consent }
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const ENABLED = String(process.env.REACT_APP_ENABLE_META_TRACKING || "").toLowerCase() === "true";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

let _pixelInjected = false;

function _uuid() {
  // RFC4122 v4-ish, ok per event_id.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function _consentGiven() {
  // Opt-out marketing tracking: il Pixel/CAPI partono se la build ha
  // ENABLE_META_TRACKING=true + PIXEL_ID valorizzato, A MENO CHE l'utente
  // non abbia disabilitato esplicitamente il consenso impostando
  //   window.__ccx_marketing_consent = false
  // La disclosure del tracking è coperta dalla Privacy/Cookie Policy;
  // chi preferisce opt-in strict può settare quel flag a `false` di
  // default dal futuro cookie banner.
  if (typeof window === "undefined") return false;
  if (!ENABLED || !PIXEL_ID) return false;
  return window.__ccx_marketing_consent !== false;
}

export function getTrackingStatus() {
  return {
    enabled: ENABLED && !!PIXEL_ID,
    pixelId: PIXEL_ID ? `${PIXEL_ID.slice(0, 3)}…${PIXEL_ID.slice(-2)}` : "",
    consent: _consentGiven(),
  };
}

function _injectPixel() {
  if (_pixelInjected) return;
  if (!ENABLED || !PIXEL_ID) return;
  if (typeof window === "undefined") return;
  if (window.fbq) { _pixelInjected = true; return; }

  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  try {
    window.fbq("init", PIXEL_ID);
    _pixelInjected = true;
  } catch (_) { /* silent */ }
}

/**
 * Inizializza il Pixel Meta se ENABLE_META_TRACKING=true e PIXEL_ID è valorizzato.
 * Idempotente. Da chiamare una sola volta al bootstrap del layout marketing.
 */
export function initMetaTracking() {
  if (!_consentGiven()) return;
  _injectPixel();
}

async function _sendCapi(eventName, eventId, params) {
  if (!ENABLED || !BACKEND_URL) return;
  try {
    await fetch(`${BACKEND_URL}/api/marketing/meta/capi`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // credentials: "omit" per non inviare cookie a un endpoint pubblico
      credentials: "omit",
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: typeof window !== "undefined" ? window.location.href : undefined,
        custom_data: params || {},
      }),
    });
  } catch (_) { /* silent — mai bloccare la UX */ }
}

function _emit(eventName, params) {
  if (!_consentGiven()) return;
  _injectPixel();
  const eventId = _uuid();
  // Pixel side
  try {
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", eventName, params || {}, { eventID: eventId });
    }
  } catch (_) { /* silent */ }
  // CAPI side (server-side dedup on the same event_id)
  _sendCapi(eventName, eventId, params);
}

export function trackPageView(extra = {}) { _emit("PageView", extra); }
export function trackViewContent(extra = {}) { _emit("ViewContent", extra); }
export function trackLead(extra = {}) { _emit("Lead", extra); }
export function trackContact(extra = {}) { _emit("Contact", extra); }
export function trackCompleteRegistration(extra = {}) { _emit("CompleteRegistration", extra); }
export function trackStartTrial(extra = {}) { _emit("StartTrial", extra); }
export function trackSchedule(extra = {}) { _emit("Schedule", extra); }
export function trackInitiateCheckout(extra = {}) { _emit("InitiateCheckout", extra); }
export function trackPurchase(extra = {}) { _emit("Purchase", extra); }
